import * as React from "react";

/**
 * What kind of box am I standing in?
 *
 * 🔴 THE POINT: a table's chrome is chosen BY ITS CONTEXT, never by whoever
 * happens to be writing the table. A full-onyx header is the page's own
 * furniture — it reads as the top of the screen. The same header inside a card
 * or a drawer reads as a second page glued into the first, so a sub-table gets
 * a lighter tinted header instead. Leaving that to a `variant` prop guarantees
 * the two eventually disagree: the same component, moved from a page into a
 * drawer, keeps the header it was authored with and nobody notices.
 *
 * RESOLUTION ORDER, most specific first:
 *   1. An explicit prop on the component (an escape hatch, rarely right).
 *   2. React context — {@link Surface}. `Card` publishes `"card"` from here,
 *      so anything inside a card is correct with no wiring at all.
 *   3. A DOM probe of the ancestors, run before paint. This is what makes
 *      drawers work without editing them: `ResourceDrawer` is a `role="dialog"`
 *      panel, so a sub-table rendered into one resolves `"drawer"` even though
 *      the drawer publishes no context of its own.
 *   4. `"page"` — the default, and the right answer for a bare route body.
 *
 * The probe is deliberately a LAST resort. Context is synchronous and survives
 * portals; the probe needs a real element, so it cannot run on the first render
 * pass. It runs in a layout effect (before paint, like `Tooltip`'s measure), so
 * the corrected chrome is what actually reaches the screen — but in jsdom, and
 * in any environment without layout, only steps 1, 2 and 4 are live.
 */
export type SurfaceKind = "page" | "card" | "drawer";

const SurfaceContext = React.createContext<SurfaceKind | null>(null);

export interface SurfaceProps {
  kind: SurfaceKind;
  children: React.ReactNode;
}

/**
 * Declare the surface for everything below. Use it where the container is not
 * one of ours — a hand-rolled panel, a third-party shell — so descendants stop
 * guessing.
 */
export function Surface({ kind, children }: SurfaceProps) {
  return <SurfaceContext.Provider value={kind}>{children}</SurfaceContext.Provider>;
}

/** The surface published by an ancestor, or null when nobody has said. */
export function useSurfaceContext(): SurfaceKind | null {
  return React.useContext(SurfaceContext);
}

/** Ancestors that answer the question, in the order they are trusted. */
const SURFACE_SELECTOR = '[data-phx-surface],[role="dialog"]';

function probe(el: Element | null): SurfaceKind | null {
  const host = el?.closest(SURFACE_SELECTOR);
  if (!host) return null;
  const declared = host.getAttribute("data-phx-surface");
  if (declared === "page" || declared === "card" || declared === "drawer") return declared;
  // A dialog with no declaration is a drawer or a modal; both are "not the
  // page", which is the only distinction the chrome actually turns on.
  return host.getAttribute("role") === "dialog" ? "drawer" : null;
}

/**
 * Resolve the surface for a component rooted at `ref`.
 *
 * @param ref      the component's own root element
 * @param override an explicit caller choice, which always wins
 */
export function useSurface(
  ref: React.RefObject<HTMLElement | null>,
  override?: SurfaceKind,
): SurfaceKind {
  const fromContext = useSurfaceContext();
  const [probed, setProbed] = React.useState<SurfaceKind | null>(null);

  const skipProbe = override != null || fromContext != null;

  React.useLayoutEffect(() => {
    if (skipProbe) return;
    setProbed(probe(ref.current));
  }, [skipProbe, ref]);

  return override ?? fromContext ?? probed ?? "page";
}
